import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import {
  ConfigDataLoadedFromWordPress,
  DocumentDataLoadedFromWordPress,
  ExpectedDataToLoadConfig,
  ExpectedDataToLoadConfigV,
  ExpectedDataToLoadDocument,
  ExpectedDataToLoadDocumentV
} from "@/types/load";
import {
  configStore,
  documentStore,
  uiStore,
  globalStore,
  browseModeStore
} from "@/store/store";
import { initAPI } from "@/lib/api/api";
import defensiveCopy from "@/util/defensive-copy";
import { breakdanceUndo } from "@/lib/undo/undo";
import { Either, fold } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import { _Error, logErrorsToUiStore } from "@/lib/wordpress/data";
import { loadAllDynamicDataFromTreeIntoStore } from "@/lib/dynamic-data/load";
import { Store } from "vuex";
import {
  precompileTemplatesOfElements,
  precompileTwigMacrosTemplate
} from "@/lib/twig/twig";
import { setWindowBreakdanceObjectAssetsFunctionsForBuilderMode } from "@/lib/canvas/helpers";
import * as t from "io-ts";
import { TemplatePreviewableItem } from "@/types/templates";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { enableBrowseMode } from "@/lib/browse-mode";
import { BreakdanceWindowObjectDataFromLoaderForBrowseMode } from "@/types/breakdance-window-object";
import { BuilderElementV } from "@/types/elements";
import NotificationLogger from "@/lib/notifications/notifications";
import { chunk } from "lodash";
import { breakdanceStoreActions } from "@/store/actions/store-actions";

export async function loadDocumentFromWordPress(
  id: number
): Promise<Either<_Error[], ExpectedDataToLoadDocument>> {
  return await makeWordPressAjaxRequest({
    action: "breakdance_load_document",
    dataToPost: { id: id },
    decoder: ExpectedDataToLoadDocumentV
  });
}

export async function loadConfigFromWordPress(): Promise<
  Either<_Error[], ExpectedDataToLoadConfig>
> {
  return await makeWordPressAjaxRequest({
    action: "breakdance_load_document",
    decoder: ExpectedDataToLoadConfigV
  });
}

/**
 * Initial mode is browse mode, loads config from WordPress
 */
export function initializeAppWhenInitialModeIsBrowse(
  maybeLoadedData: Either<_Error[], ExpectedDataToLoadConfig>,
  browseModeOptions?: Pick<
    BreakdanceWindowObjectDataFromLoaderForBrowseMode,
    "browseModeOpenUrl" | "returnUrl"
  >
) {
  pipe(
    maybeLoadedData,
    fold(
      errors => {
        uiStore.setInterfaceState("normal");

        logErrorsToUiStore(
          errors.map(err => {
            return {
              ...err,
              customNotificationToShow: err.customNotificationToShow
                ? err.customNotificationToShow.type === "INTERNAL_SERVER_ERROR"
                  ? { ...err.customNotificationToShow, keepOpen: true }
                  : err.customNotificationToShow
                : undefined
            };
          })
        );
      },
      async loadedData => {
        initializeBreakdanceWindowObject();
        initAPI(loadedData.plugins);

        putDataInStoreFromWordPressConfig(loadedData);

        if (browseModeOptions?.returnUrl) {
          browseModeStore.setBackToAdminUrl(browseModeOptions.returnUrl);
        }

        precompileTwigMacrosTemplate();
        precompileTemplatesOfElements();

        uiStore.selectBaseBreakpoint();

        enableBrowseMode(
          "globalSettings",
          browseModeOptions?.browseModeOpenUrl
        );
      }
    )
  );
}

/**
 * Initial mode is builder, loads both document data and config from WordPress
 */
export function initializeAppWhenInitialModeIsBuilder(
  maybeLoadedData: Either<_Error[], ExpectedDataToLoadDocument>
) {
  pipe(
    maybeLoadedData,
    fold(
      errors => {
        uiStore.setInterfaceState("normal");

        logErrorsToUiStore(
          errors.map(err => {
            return {
              ...err,
              customNotificationToShow: err.customNotificationToShow
                ? err.customNotificationToShow.type === "INTERNAL_SERVER_ERROR"
                  ? { ...err.customNotificationToShow, keepOpen: true }
                  : err.customNotificationToShow
                : undefined
            };
          })
        );
      },
      async loadedData => {
        if ("errorType" in loadedData) {
          uiStore.setInterfaceState("normal");
          configStore.setInvalidPostData(loadedData);

          return;
        }

        initializeBreakdanceWindowObject();
        setWindowBreakdanceObjectAssetsFunctionsForBuilderMode();

        initAPI(loadedData.plugins);

        putDataInStoreFromDocumentLoadedFromWordpress(loadedData);

        precompileTwigMacrosTemplate();
        precompileTemplatesOfElements();

        await loadAllDynamicDataFromTreeIntoStore();

        uiStore.selectBaseBreakpoint();
        uiStore.setInterfaceState("normal");

        breakdanceUndo.initialize();
      }
    )
  );
}

export function putDataInStoreFromWordPressConfig(
  d: ConfigDataLoadedFromWordPress
) {
  configStore.setEnvironment(d.environment);
  configStore.setElementsAndAppendRootElement(d.builderElements);
  configStore.setElementsExternalAttrsDepsAndActions(
    d.elementsExternalAttrsDepsAndActions
  );
  configStore.setElementCategories(d.builderElementCategories);
  configStore.setPreferences(d.preferences);
  configStore.setDynamicFields(d.dynamicFields);
  configStore.setConditions(d.conditions);
  configStore.setFonts(d.fonts);
  configStore.setGlobalSettingsControlsAndTemplate(
    d.globalSettingsControlsAndTemplate
  );
  configStore.setAvailableDocuments(d.availableDocuments);
  configStore.setAvailableMediaSizes(d.availableMediaSizes);
  configStore.setAvailablePermissions(d.availablePermissions);
  configStore.setPermission(d.permission);
  configStore.setSelectorControls(d.selectors.controls);
  configStore.setClassTemplate(d.selectors.template);
  configStore.setNeededUrls(d.neededData);
  configStore.setPresetsForElements(d.elementPresets);
  configStore.setBuiltinBreakpoints(d.builtinBreakpoints);
  configStore.setTwigMacros(d.twigMacros);
  configStore.setTwigMacrosHash(d.twigMacrosHash);
  configStore.setAvailableWordpressPlugins(d.availableWordpressPlugins);
  configStore.setDesignLibrary(d.designLibrary);
  configStore.setSubscriptionMode(d.subscriptionMode);

  breakdanceUndo.ignore(() => {
    globalStore.setGlobalSettings(d.globalSettings);
    globalStore.markCurrentGlobalSettingsAsUntouched();

    globalStore.setSelectors(d.selectors.selectors);
    globalStore.markCurrentSelectorsAsUntouched();
  });
}

export function putDataInStoreFromDocumentLoadedFromWordpress(
  d: DocumentDataLoadedFromWordPress
) {
  documentStore.setDocument(d.document);

  configStore.setDocumentSpecificNeededUrls(d.documentSpecificNeededUrls);
  configStore.loadTemplatePreviewData(d.templatePreview);

  putDataInStoreFromWordPressConfig(d);
}

function initializeBreakdanceWindowObject() {
  // Save the URL on first page load. The URL may change later and we want to be able to use it
  // For example if we by using a Router
  window.Breakdance.originalUrl = location.href;

  window.Breakdance.stores = {
    configStore,
    documentStore,
    uiStore,
    globalStore
  };
}

export function getDocumentUrlFromId(documentId: number) {
  const updatedUrl = new URL(window.Breakdance.originalUrl);

  updatedUrl.searchParams.set("id", `${documentId}`);
  updatedUrl.search = updatedUrl.searchParams.toString();

  return updatedUrl.href;
}

export async function saveLastPreviewedItemInWp(
  reviewableItem: TemplatePreviewableItem
) {
  return makeWordPressAjaxRequest({
    action: "breakdance_save_template_last_previewed_item",
    decoder: t.type({ success: t.literal(true) }),
    dataToPost: {
      postId: documentStore.document.documentMeta.id,
      item: fpJsonStringify(reviewableItem)
    }
  });
}

class InitialStoreState {
  initialState: unknown;
  store!: Store<any>;

  setupHook(store: Store<any>) {
    this.store = store;
    this.initialState = defensiveCopy(this.store.state);
  }

  resetToInitialState() {
    // TODO: but what about the AJX errors? lmfao.
    const newState = defensiveCopy(this.initialState);
    this.store.replaceState(newState);
  }
}

export const initialStoreState = new InitialStoreState();

export const vuexInitialStoreStateInitFunction = (store: Store<any>) => {
  initialStoreState.setupHook(store);
};

export async function loadBuilderElementsFromWordPress() {
  pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_load_builder_elements",
      resultKey: "elements",
      decoder: t.array(BuilderElementV)
    }),
    fold(
      errors => {
        errors.forEach(e => NotificationLogger.log.message(e.msg));
        configStore.setElementsLoadingState("failed");
        uiStore.logError("Failed to load elements");
      },
      elements => {
        const chunked = chunk(elements, 10);

        chunked.forEach((elementsChunk, index) => {
          // @ts-ignore
          requestIdleCallback(() => {
            configStore.addNonExistentElementsNonReactively(elementsChunk);
            precompileTemplatesOfElements(elementsChunk);

            const isLastChunk = index === chunked.length - 1;
            if (isLastChunk) {
              // TODO this clears and re-registers store actions for elements that have been initially loaded, should it only affect newly loaded elements?
              breakdanceStoreActions.registerAllStoreActions();
              configStore.setElementsLoadingState("success");
            }
          });
        });
      }
    )
  );
}
